import React from 'react';
import { Box, Typography, Checkbox, IconButton, TextField } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { CheckCircle, FolderOpen, Edit } from '@material-ui/icons';
import { ProgramsAnalyseResponse, ProgramSuggestion } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    resultsContainer: {
      maxHeight: '450px',
      overflow: 'auto',
    },
    summaryBox: {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    resultSection: {
      marginBottom: theme.spacing(3),
    },
    resultSectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      color: theme.palette.text.primary,
    },
    programCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
    },
    programCardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    programIcon: {
      color: theme.palette.primary.main,
      fontSize: 20,
      marginRight: 6,
    },
    programName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      flex: 1,
    },
    programDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginLeft: theme.spacing(3.5),
      marginBottom: theme.spacing(0.5),
    },
    programReason: {
      color: theme.palette.text.disabled,
      fontSize: '0.75rem',
      marginLeft: theme.spacing(3.5),
      fontStyle: 'italic',
    },
    editField: {
      marginLeft: theme.spacing(3.5),
      marginTop: theme.spacing(1),
    },
  })
);

interface ProgramsResultsViewProps {
  results: ProgramsAnalyseResponse;
  selectedPrograms: Set<string>;
  editedPrograms: Map<string, { name: string; description: string }>;
  onToggleProgram: (programName: string) => void;
  onEditProgram: (originalName: string, newName: string, newDescription: string) => void;
}

export function ProgramsResultsView({
  results,
  selectedPrograms,
  editedPrograms,
  onToggleProgram,
  onEditProgram,
}: ProgramsResultsViewProps) {
  const classes = useStyles();
  const [editingProgram, setEditingProgram] = React.useState<string | null>(null);

  const handleEditClick = (e: React.MouseEvent, programName: string) => {
    e.stopPropagation();
    setEditingProgram(editingProgram === programName ? null : programName);
  };

  const getDisplayValues = (program: ProgramSuggestion) => {
    const edited = editedPrograms.get(program.name);
    return {
      name: edited?.name ?? program.name,
      description: edited?.description ?? program.description ?? '',
    };
  };

  return (
    <Box className={classes.resultsContainer}>
      {/* Summary */}
      <Box className={classes.summaryBox}>
        <Box display='flex' alignItems='center' mb={1}>
          <CheckCircle style={{ color: '#4caf50', marginRight: 8 }} />
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
            Analysis Complete
          </Typography>
        </Box>
        <Typography variant='body2' color='textSecondary'>
          {results.analysis.summary}
        </Typography>
      </Box>

      {/* Programs */}
      {results.programs && results.programs.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>
            <FolderOpen className={classes.programIcon} />
            Programs to Create ({selectedPrograms.size} of {results.programs.length} selected)
          </Typography>
          {results.programs.map((program) => {
            const displayValues = getDisplayValues(program);
            const isEditing = editingProgram === program.name;

            return (
              <Box
                key={program.name}
                className={classes.programCard}
                onClick={() => onToggleProgram(program.name)}
                style={{ opacity: selectedPrograms.has(program.name) ? 1 : 0.6 }}
              >
                <Box className={classes.programCardHeader}>
                  <Checkbox
                    checked={selectedPrograms.has(program.name)}
                    color='primary'
                    size='small'
                    style={{ padding: 0, marginRight: 8 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleProgram(program.name)}
                  />
                  <FolderOpen className={classes.programIcon} />
                  <Typography className={classes.programName}>{displayValues.name}</Typography>
                  <IconButton size='small' onClick={(e) => handleEditClick(e, program.name)} style={{ opacity: 0.6 }}>
                    <Edit fontSize='small' />
                  </IconButton>
                </Box>

                {!isEditing && displayValues.description && (
                  <Typography className={classes.programDescription}>{displayValues.description}</Typography>
                )}

                {!isEditing && <Typography className={classes.programReason}>Source: {program.reason}</Typography>}

                {isEditing && (
                  <Box className={classes.editField} onClick={(e) => e.stopPropagation()}>
                    <TextField
                      fullWidth
                      size='small'
                      label='Program Name'
                      value={displayValues.name}
                      onChange={(e) => onEditProgram(program.name, e.target.value, displayValues.description)}
                      style={{ marginBottom: 8 }}
                    />
                    <TextField
                      fullWidth
                      size='small'
                      label='Description (optional)'
                      value={displayValues.description}
                      onChange={(e) => onEditProgram(program.name, displayValues.name, e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* No programs found */}
      {(!results.programs || results.programs.length === 0) && (
        <Box className={classes.resultSection}>
          <Typography variant='body2' color='textSecondary'>
            No programs were identified from the provided documents. You can create programs manually later.
          </Typography>
        </Box>
      )}

      {/* Recommendations */}
      {results.recommendations && results.recommendations.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>Recommendations</Typography>
          <Box component='ul' style={{ margin: 0, paddingLeft: 20 }}>
            {results.recommendations.map((rec) => (
              <li key={rec}>
                <Typography variant='body2' color='textSecondary'>
                  {rec}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Helper to get initial selections from response
export function getInitialProgramSelections(response: ProgramsAnalyseResponse) {
  return {
    programs: new Set(response.programs.map((p) => p.name)),
  };
}

// Helper to build apply summary
export function getProgramsApplySummary(selectedPrograms: Set<string>): string {
  if (selectedPrograms.size === 0) {
    return 'No programs selected';
  }
  return `${selectedPrograms.size} program${selectedPrograms.size !== 1 ? 's' : ''} will be created`;
}
